import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { type GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import classNames from 'classnames';
import { useI18nContext } from '@procore/core-react';
import Footer from '../../components/Layout/Footer';
import Header from '../../components/Layout/Header';
import LinkList from '../../components/Layout/LinkList';
import routes from '../../helpers/routes';
import { type CountryType, findCountry } from '../../data/location/countries';
import CountryMap from '../../components/Maps/CountryMap';
import { getStates, type TopStatesResult } from '../../services/states';
import { doesCountryHaveSVGData } from '../../data/location/state-images';
import { getStateList } from '../../data/location/states';
import { useStyles } from './index.styles';
import FrequentlyAskedQuestions from '../../components/FAQ/FrequentlyAskedQuestions';
import { usePageView } from '../../components/Analytics/utilities';
import { type FaqData, generateFaqs } from '../../helpers/generate-faqs';
import { setupI18next } from '../../helpers/setup-i18next';

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { country } = context.query;

  if ((country as string).length > 2) {
    return {
      notFound: true,
    };
  }
  const countryType = findCountry(country as string);
  if (!countryType || !countryType.enabled) {
    return {
      notFound: true,
    };
  }
  const states = await getStates(countryType);
  const totalResults = states.reduce((res, cur) => res + cur.count, 0);
  const I18n = setupI18next(context.locale);
  const faqs = generateFaqs(
    totalResults,
    null,
    null,
    null,
    {
      country: countryType,
      state: null,
      city: null,
    },
    I18n
  );

  return {
    props: {
      country: countryType,
      states,
      faqs,
    },
  };
}

interface CountryPageProps {
  country: CountryType;
  states: TopStatesResult[];
  faqs: FaqData[];
}

export default function CountryPage({
  country,
  states,
  faqs,
}: CountryPageProps) {
  const I18n = useI18nContext();
  const classes = useStyles();
  usePageView(`${country.alpha2}`);

  const showMap = doesCountryHaveSVGData(country);
  const countryName = country.preferredName || country.name;
  const countryNameInSentence =
    country.alpha2 === 'US' ? ` the ${countryName}` : countryName;

  return (
    <>
      <Head>
        <title>
          {I18n.t('pages.country.directory', {
            name: countryName,
          })}
        </title>
        <meta
          name="description"
          content="Search for subcontractors, general contractors, owners and architects. Procore has thousands of construction professionals ready to connect."
        />
      </Head>
      <main>
        <Header />
        {showMap && (
          <Box
            className={classNames(
              classes.sectionContainer,
              classes.topContainer
            )}
          >
            <Container>
              <Typography component="h1" variant="h2">
                {I18n.t('pages.country.search', {
                  name: countryNameInSentence,
                })}
              </Typography>
              <Typography className={classes.subtitle} component="h2">
                {I18n.t('pages.country.click', {
                  stateOrProvince:
                    country.alpha2 === 'US' ? 'state' : 'province',
                })}
              </Typography>
              <CountryMap country={country} states={states} />
            </Container>
          </Box>
        )}
        <Box
          className={classNames(
            classes.sectionContainer,
            !showMap ? classes.topContainer : ''
          )}
        >
          <Container>
            <LinkList
              title={
                country.alpha2 === 'US'
                  ? 'Construction Professionals by State'
                  : `Construction Professionals in ${country.name} by Province`
              }
              links={getStateList(country)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((state) => {
                  const count = states
                    .find((result) => result.state === state.abbreviation)
                    ?.count?.toLocaleString();
                  return {
                    label: `Contractors in ${state.name} (${count || 0})`,
                    href: routes.location(country.alpha2, state.abbreviation),
                  };
                })}
              headerElement={showMap ? 'h2' : 'h1'}
              canViewMore={false}
            />
          </Container>
        </Box>
        <Box className={classes.sectionContainer}>
          <FrequentlyAskedQuestions faqs={faqs} />
        </Box>
      </main>
      <Footer />
    </>
  );
}
